import { getIncomers } from '@xyflow/react';
import { getTextFromTextNodes, getPromptFromUploadNodes, getSubjectSystemHintsFromUploadNodes, getUploadNodeImages, isAssetRefSupportedModel } from '@/lib/xyflow';
import { handleError } from '@/lib/error/handle';
import { videoModels } from '@flowmuse/models/video';
import { buildProviderOptions } from '@flowmuse/models/video/ui-options';
import { env } from '@/lib/env';
import { readVideoMeta } from '@/lib/editor-url';
import { ensureProjectSavedOrShowRefreshModal } from '@/lib/project-save';
import { resolveDotPath, normalizeAutoNumber, normalizeAutoString } from '@flowmuse/models/video/tools';
import { mergeInlineMedia, defaultInlineMediaForMode, getMediaDurationFromUrl } from '@flowmuse/models/inline-attachments';
import { checkConcurrency } from '@/lib/check-concurrency';
import { normalizeMentions } from '@/components/nodes/shared/normalize-mentions';

function resolveAssetRefUrl(url: string, nodeData: any): string {
    if (!url || !url.startsWith('Asset://')) return url;
    const preview = nodeData?.assetPreviewUrl;
    return typeof preview === 'string' && preview ? preview : url;
}

type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

// precheck 缺失输入时抛出的专用错误：携带缺失项（prompt/image/video/audio/taskId/alts）与可读消息，
// 交给调用方决定是弹窗提醒还是走默认 toast。
export class PrecheckError extends Error {
    missing: string;
    constructor(missing: string, message: string) {
        super(message);
        this.name = 'PrecheckError';
        this.missing = missing;
    }
}

export type CreateVideoHandleGenerateParams = {
    project: { id?: string } | null;
    nodeId: string;
    modelId: string;
    data: any;
    getNodes: () => any[];
    getEdges: () => any[];
    updateNodeData: (id: string, partial: any) => void;
    t: TranslateFn;
    // 交互式生成（点击按钮）时传入：precheck 缺失输入时回调，由调用方弹窗提醒，不再走 toast/failed。
    onPrecheckFail?: (missing: string, message: string) => void;
    // precheck 通过、即将开始耗时操作（保存/请求）时回调，供调用方此时才显示 loading，
    // 避免 precheck 失败也闪一下转圈。
    onBeforeGenerate?: () => void;
};

export const createVideoHandleGenerate = (params: CreateVideoHandleGenerateParams) => {
    const { project, nodeId, modelId, data, getNodes, getEdges, updateNodeData, t, onPrecheckFail, onBeforeGenerate } = params;
    const handleGenerate = async () => {
        try {
            /*
            预校验
            1. 项目 ID
            2. 节点 ID
            3. 模型 ID
            4. 节点状态为 processing
            */
            if (!project?.id) {
                throw new Error(t('Project not found'));
            }
            if (!nodeId) {
                throw new Error(t('Node not found'));
            }
            if (!modelId) {
                throw new Error(t('Model not found'));
            }
            if ((data as any)?.status === 'processing') {
                throw new Error(t('Loading'));
            }

            /*
            收集数据
            1. 收集上游节点数据
            2. 收集 frameMode
            3. 收集 textPrompts
            */
            // 收集 incomers, 兜底 []
            const incomers = getIncomers({ id: nodeId }, getNodes(), getEdges());
            // 收集 frameMode, 兜底 'none'
            const frameMode = ((data as any)?.frameMode as string | undefined) ?? 'none';
            // 收集 textPrompts, 兜底 []
            const textPrompts = getTextFromTextNodes(incomers);

            /*
            通过 ID 收集上游节点的图片/视频/音频, 得到
            1. 图像: upstreamImages
            2. 视频: upstreamVideos
            3. 音频: upstreamAudios
            */
            // 图像 Map: Key: rid, Value: { url: string; type: string; rid: string }
            // rid 约定：
            //   普通上游：up:${nodeId}
            //   multi 主体上游：up:${nodeId}（首图，向后兼容）+ up:${nodeId}:1, :2, ... （其余原图）
            const upstreamBySourceId = new Map<string, { url: string; type: string; rid: string }>();
            // 视频 Array: { url: string; type: string; rid: string; processingTaskId?: string }
            const upstreamVideos: { url: string; type: string; rid: string; processingTaskId?: string }[] = [];
            // 音频 Array: { url: string; type: string; rid: string }
            const upstreamAudios: { url: string; type: string; rid: string }[] = [];
            // 遍历上游节点
            for (const inc of incomers as any[]) {
                // 获取当前节点类型
                const nodeType = String(inc.type || '');
                // 获取当前节点数据
                const d = inc.data as any;
                // 获取当前节点 ID
                const id = String(inc.id);
                const supportsAssetRef = isAssetRefSupportedModel(modelId);
                // 判断当前节点类型为 image 或 upload (image)
                if (nodeType === 'image' || (nodeType === 'upload' && d?.fileType === 'image')) {
                    // upload 节点支持 multi 主体展开（subjectImages 多张原图）
                    if (nodeType === 'upload') {
                        const expanded = getUploadNodeImages(d);
                        if (expanded.length > 1) {
                            expanded.forEach((img, i) => {
                                let url = img.url;
                                if (url && !supportsAssetRef) url = resolveAssetRefUrl(url, d);
                                if (!url) return;
                                const rid = i === 0 ? `up:${id}` : `up:${id}:${i}`;
                                upstreamBySourceId.set(rid, { url, type: img.type || 'image/png', rid });
                            });
                        } else {
                            let url = d?.generated?.url || d?.generated?.urls?.[0] || d?.content?.url;
                            if (url && !supportsAssetRef) url = resolveAssetRefUrl(url, d);
                            const mimeType = d?.generated?.type || d?.content?.type || 'image/png';
                            if (url) upstreamBySourceId.set(`up:${id}`, { url, type: mimeType, rid: `up:${id}` });
                        }
                    } else {
                        let url = d?.generated?.url || d?.generated?.urls?.[0] || d?.content?.url;
                        if (url && !supportsAssetRef) url = resolveAssetRefUrl(url, d);
                        const mimeType = d?.generated?.type || d?.content?.type || 'image/png';
                        if (url) upstreamBySourceId.set(`up:${id}`, { url, type: mimeType, rid: `up:${id}` });
                    }
                // 判断当前节点类型为 video 或 upload (video)
                } else if (nodeType === 'video' || (nodeType === 'upload' && d?.fileType === 'video')) {
                    const video = d?.generated || d?.content;
                    const mimeType = video?.type || 'video/mp4';
                    let vUrl = video?.url;
                    if (vUrl && !supportsAssetRef) vUrl = resolveAssetRefUrl(vUrl, d);
                    if (vUrl) upstreamVideos.push({ url: vUrl, type: mimeType, rid: `up:${id}`, ...(d?.processingTaskId ? { processingTaskId: d.processingTaskId } : {}) });
                // 判断当前节点类型为 audio 或 upload (audio)
                } else if (nodeType === 'audio' || (nodeType === 'upload' && d?.fileType === 'audio')) {
                    const audio = d?.generated || d?.content;
                    const mimeType = audio?.type || 'audio/mpeg';
                    let aUrl = audio?.url;
                    if (aUrl && !supportsAssetRef) aUrl = resolveAssetRefUrl(aUrl, d);
                    if (aUrl) upstreamAudios.push({ url: aUrl, type: mimeType, rid: `up:${id}` });
                }
            }
            // 图像 Map 转换为 图像 Array
            const upstreamImages = [...upstreamBySourceId.values()];

            /*
            收集内连图片/视频/音频, 得到
            1. 图像: inlineByType[image]
            2. 视频: inlineByType[video]
            3. 音频: inlineByType[audio]
            */
            // 收集内连附件, 兜底 []
            const inlineAtts = Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments as any[] : [];
            // 收集模型配置
            const modelMeta = videoModels[modelId];
            // 收集内连媒体配置, 优先级: merge(modelMeta.inlineMedia, modelMeta.configByMode[frameMode].inlineMedia) > defaultInlineMediaForMode(frameMode)
            const imCfg = mergeInlineMedia(modelMeta?.inlineMedia, modelMeta?.configByMode?.[frameMode]?.inlineMedia)
                ?? defaultInlineMediaForMode(frameMode);
            // 默认 MIME Types
            const defaultMimes: Record<string, string> = { video: 'video/mp4', audio: 'audio/mpeg', image: 'image/png' };
            // 内连媒体对象: { image: { url: string; type: string, rid: string }[], video: { url: string; type: string, rid: string }[], audio: { url: string; type: string, rid: string }[] }
            const inlineByType: Record<string, { url: string; type: string; rid: string }[]> = { image: [], video: [], audio: [] };
            // 遍历内连数组
            const supportsAssetRefInline = isAssetRefSupportedModel(modelId);
            for (const a of inlineAtts) {
                // 跳过缺失 URL 或 MIME Type 的附件
                if (!a?.url || !a?.fileType) continue;
                // 检查附件类型在配置中的允许情况, 不允许则跳过
                const tc = imCfg[a.fileType as 'image' | 'video' | 'audio'];
                if (!tc?.allow) continue;
                let attUrl: string = a.url;
                if (!supportsAssetRefInline) attUrl = resolveAssetRefUrl(attUrl, a);
                // 添加到内连媒体对象中
                (inlineByType[a.fileType] ??= []).push({ url: attUrl, type: a.type || defaultMimes[a.fileType], rid: `in:${a.id}` });
            }

            // orderKey 排序函数
            const sortByRidOrder = <T extends { rid: string }>(arr: T[], order: string[]) => {
                const idx = new Map(order.map((r, i) => [r, i]));
                arr.sort((a, b) => {
                    const ai = idx.get(a.rid);
                    const bi = idx.get(b.rid);
                    if (ai !== undefined && bi !== undefined) return ai - bi;
                    if (ai !== undefined) return -1;
                    if (bi !== undefined) return 1;
                    return 0;
                });
            };

            // ref/avatar 模式下，用户在 UI 中混合拖拽所有媒体，顺序统一存在 referenceImageOrder
            const isRefLike = frameMode === 'ref' || frameMode === 'avatar';
            const refOrder = isRefLike ? (data as any)?.referenceImageOrder as string[] | undefined : undefined;

            // 视频排序 + 截断
            let allVideos: typeof upstreamVideos;
            // 不允许视频, 则 allVideos 为空数组
            if (!imCfg.video?.allow) {
                allVideos = [];
            } else {
                // 允许视频, 则 allVideos 为上游视频 + 内连视频
                allVideos = [...upstreamVideos, ...inlineByType.video];
                // 视频排序 key, ref 模式下为 referenceImageOrder, 否则为 videoOrder
                const videoOrder = isRefLike ? refOrder : (data as any)?.videoOrder as string[] | undefined;
                // 视频排序 key 存在且长度大于 0 则按 rid 排序
                if (Array.isArray(videoOrder) && videoOrder.length > 0) {
                    sortByRidOrder(allVideos, videoOrder);
                }
                // 视频最大数量
                const vidMax = imCfg.video.maxCount ?? Infinity;
                // 截断
                if (Number.isFinite(vidMax)) {
                    allVideos = allVideos.slice(0, vidMax);
                }
            }

            // 音频排序 + 截断
            let allAudios: typeof upstreamAudios;
            // 不允许音频, 则 allAudios 为空数组
            if (!imCfg.audio?.allow) {
                allAudios = [];
            } else {
                // 允许音频, 则 allAudios 为上游音频 + 内连音频
                allAudios = [...upstreamAudios, ...inlineByType.audio];
                // 音频排序 key, ref 模式下为 referenceImageOrder, 否则为 audioOrder
                const audioOrder = isRefLike ? refOrder : (data as any)?.audioOrder as string[] | undefined;
                // 音频排序 key 存在且长度大于 0 则按 rid 排序
                if (Array.isArray(audioOrder) && audioOrder.length > 0) {
                    sortByRidOrder(allAudios, audioOrder);
                }
                // 音频最大数量
                const audMax = imCfg.audio.maxCount ?? Infinity;
                // 截断
                if (Number.isFinite(audMax)) {
                    allAudios = allAudios.slice(0, audMax);
                }
            }

            // 图片排序 + 截断
            let allImages: typeof upstreamImages;
            // 不允许图片, 则 allImages 为空数组
            if (!imCfg.image?.allow) {
                allImages = [];
            } else {
                // 允许图片, 则 allImages 为上游图片 + 内连图片
                allImages = [...upstreamImages, ...inlineByType.image];
                // 图片排序 key, ref 模式下为 referenceImageOrder, 否则为 frameImageOrder
                const orderKey = isRefLike ? refOrder : (data as any)?.frameImageOrder as string[] | undefined;
                // 图片排序 key 存在且长度大于 0 则按 rid 排序
                if (Array.isArray(orderKey) && orderKey.length > 0) {
                    sortByRidOrder(allImages, orderKey);
                // 兼容逻辑: 若无 orderKey, 则按 frameRole + edge 顺序排
                } else if ((data as any)?.edgeFrameRoles) {
                    const mirror = (data as any).edgeFrameRoles as Record<string, string>;
                    const roleSlots: string[] = frameMode === 'first' ? ['start'] : ['start', 'end'];
                    const incomingEdges = (getEdges() as any[]).filter(e => String(e.target) === nodeId);
                    const edgeOrderByUrl = new Map<string, number>();
                    const roleByUrl = new Map<string, string>();
                    let oi = 0;
                    for (const e of incomingEdges) {
                        const sourceId = String(e.source);
                        // multi 主体节点会贡献多个 entries（rid: up:id / up:id:1 / up:id:2 ...）
                        // 全部按入边顺序累加；frameRole 只对单图上游有意义
                        const entriesFromSource: { url: string; type: string; rid: string }[] = [];
                        for (const v of upstreamBySourceId.values()) {
                            if (v.rid === `up:${sourceId}` || v.rid.startsWith(`up:${sourceId}:`)) {
                                entriesFromSource.push(v);
                            }
                        }
                        for (const entry of entriesFromSource) {
                            if (!edgeOrderByUrl.has(entry.url)) edgeOrderByUrl.set(entry.url, oi++);
                        }
                        const role = e.data?.frameRole ?? mirror[e.source];
                        if (role && entriesFromSource.length === 1) {
                            roleByUrl.set(entriesFromSource[0].url, role);
                        }
                    }
                    for (const a of inlineAtts) {
                        if (a?.fileType === 'image' && a?.url && a?.role) roleByUrl.set(a.url, a.role);
                    }
                    allImages.sort((a, b) => {
                        if (roleByUrl.size > 0) {
                            const ai = roleByUrl.has(a.url) ? roleSlots.indexOf(roleByUrl.get(a.url)!) : -1;
                            const bi = roleByUrl.has(b.url) ? roleSlots.indexOf(roleByUrl.get(b.url)!) : -1;
                            if (ai !== -1 && bi !== -1) return ai - bi;
                            if (ai !== -1) return -1;
                            if (bi !== -1) return 1;
                        }
                        const eai = edgeOrderByUrl.get(a.url);
                        const ebi = edgeOrderByUrl.get(b.url);
                        if (eai !== undefined && ebi !== undefined) return eai - ebi;
                        if (eai !== undefined) return -1;
                        if (ebi !== undefined) return 1;
                        return 0;
                    });
                }
                // 图片最大数量
                const imgMaxCount = imCfg.image.maxCount ?? Infinity;
                // 截断
                if (Number.isFinite(imgMaxCount)) allImages = allImages.slice(0, imgMaxCount);
            }

            // 过滤 rid 字段
            const apiImages = allImages.map(({ url, type }) => ({ url, type }));
            const apiVideos = allVideos.map(({ url, type }) => ({ url, type }));
            const apiAudios = allAudios.map(({ url, type }) => ({ url, type }));

            // 节点内提示词
            const instructions = (data as any)?.instructions || '';
            // 主体提示词
            const subjectPrompts = getPromptFromUploadNodes(incomers);
            // 主体系统提示词
            const subjectSystemHints = getSubjectSystemHintsFromUploadNodes(incomers);
            const uiDuration = normalizeAutoNumber((data as any)?.duration);
            const uiQuality = normalizeAutoString((data as any)?.quality ?? (data as any)?.resolution);
            const uiAspectRatio = normalizeAutoString((data as any)?.aspectRatio);

            // precheck 尽量前置：放在任何耗时操作（读取视频元数据 / 保存 / 并发检查）之前，
            // 这样缺输入时弹窗能立即弹出，与图像节点体验一致。
            const upstreamTaskId = (allVideos?.[0] as any)?.processingTaskId;
            const requestModelUi = { ...(data as any)?.modelUi, taskId: upstreamTaskId, frameMode };
            const providerOptions = buildProviderOptions(modelId, requestModelUi);
            // 样片转正式：提示词/素材由服务端按样片生成记录复用，无需 precheck
            const draftSource = (data as any)?.draftSource?.modelId === modelId
                ? { nodeId: String((data as any).draftSource.nodeId), videoUrl: String((data as any).draftSource.videoUrl) }
                : undefined;
            const precheckByMode = (modelMeta as any)?.precheckByMode as Record<string, string[]> | undefined;
            const precheck = draftSource
                ? []
                : (precheckByMode && frameMode && Array.isArray(precheckByMode[frameMode]))
                    ? precheckByMode[frameMode]!
                    : Array.isArray(modelMeta?.precheck) ? modelMeta.precheck : [];
            const precheckCtx: Record<string, any> = { providerOptions, modelUi: requestModelUi };
            for (const raw of precheck) {
                const [rule, ...conds] = raw.split('&');
                if (conds.length > 0 && !conds.every(c => resolveDotPath(c, precheckCtx))) continue;
                if (rule.includes('|')) {
                    const alts = rule.split('|');
                    const ok = alts.some(r => {
                        if (r === 'prompt') return !!(instructions || textPrompts.length);
                        if (r === 'image') return allImages.length > 0;
                        if (r === 'video') return allVideos.length > 0;
                        if (r === 'audio') return allAudios.length > 0;
                        if (r === 'taskId') return !!((data as any)?.processingTaskId || upstreamTaskId);
                        return true;
                    });
                    if (!ok) throw new PrecheckError(rule, t('Please input {{items}}', { items: alts.join(' / ') }));
                    continue;
                }
                if (rule === 'prompt' && (!instructions && textPrompts.length === 0)) {
                    throw new PrecheckError('prompt', t('Please input prompt'));
                }
                if (rule === 'image' && allImages.length === 0) {
                    throw new PrecheckError('image', t('Please input image'));
                }
                // 首尾帧模式：必须同时提供首帧与尾帧两张图
                if (rule === 'image2' && allImages.length < 2) {
                    throw new PrecheckError('image2', t('Please input two images'));
                }
                if (rule === 'video' && allVideos.length === 0) {
                    throw new PrecheckError('video', t('Please input video'));
                }
                if (rule === 'audio' && allAudios.length === 0) {
                    throw new PrecheckError('audio', t('Please input audio'));
                }
                if (rule === 'taskId' && !(data as any)?.processingTaskId && !upstreamTaskId) {
                    throw new Error(t('This model does not support extensions'));
                }
                if (rule.includes('.') && !resolveDotPath(rule, precheckCtx)) {
                    const label = rule.split('.').pop() ?? rule;
                    throw new Error(t('Please enable {{label}}', { label }));
                }
            }

            // 计费候选时长（拆分为三种来源：selected/audio/video）
            const durationCandidates = {
                selected: uiDuration ?? 5,
                // 基于实际发送的音频列表（allAudios：上游 + 内连，已按顺序截断）求总时长，
                // 上游节点读 durationSeconds、内连附件读 duration，未知时长的条目在线探测兜底。
                audio: (async () => {
                    try {
                        const knownByRid = new Map<string, number>();
                        for (const inc of incomers as any[]) {
                            const d = inc?.data as any;
                            const dur = Number(d?.durationSeconds ?? d?.generated?.durationSeconds);
                            if (Number.isFinite(dur) && dur > 0) knownByRid.set(`up:${String(inc.id)}`, dur);
                        }
                        for (const a of inlineAtts) {
                            const dur = Number(a?.duration ?? a?.durationSeconds);
                            if (a?.id && Number.isFinite(dur) && dur > 0) knownByRid.set(`in:${a.id}`, dur);
                        }
                        const durations = await Promise.all(
                            allAudios
                                .filter(a => a?.url)
                                .map(async (aud) => {
                                    const known = knownByRid.get(aud.rid);
                                    if (known) return known;
                                    try {
                                        const probed = await getMediaDurationFromUrl(aud.url, 'audio');
                                        return Number.isFinite(probed) && probed > 0 ? probed : 0;
                                    } catch { return 0; }
                                }),
                        );
                        const totalSeconds = durations.reduce((s, d) => s + d, 0);
                        return totalSeconds > 0 ? Math.max(1, Math.ceil(totalSeconds)) : undefined;
                    } catch { }
                    return undefined as number | undefined;
                })(),
                video: (async () => {
                    const durations = await Promise.all(
                        allVideos
                            .filter(v => v?.url)
                            .map(v => readVideoMeta(v.url).then(
                                meta => { const s = Number((meta as any)?.durationInSeconds); return Number.isFinite(s) && s > 0 ? s : 0; },
                                () => 0,
                            )),
                    );
                    const totalSeconds = durations.reduce((s, d) => s + d, 0);
                    return totalSeconds > 0 ? Math.max(1, Math.ceil(totalSeconds)) : undefined;
                })(),
            } as const;

            // ====== 生成用 duration：优先用户选择，其次模型默认，媒体真实时长单独通过 durationCandidates 传给后端 ======
            const modeOverride = (modelMeta as any)?.configByMode?.[frameMode];
            const mDurations = modeOverride?.durations ?? (modelMeta as any)?.durations;
            const mAspectRatios = modeOverride?.aspectRatios ?? (modelMeta as any)?.aspectRatios;
            const mQualities = modeOverride?.qualities ?? (modelMeta as any)?.qualities;
            const mQualityByDuration = modeOverride?.qualityByDuration ?? (modelMeta as any)?.qualityByDuration;
            const modelDurations =
                Array.isArray(mDurations) ? (mDurations as number[]) : [];
            const selectedDuration =
                uiDuration ??
                (modelDurations.length > 0 ? modelDurations[0] : undefined) ??
                durationCandidates.selected;
            // selectedAspectRatio：aspectRatios: [] => Auto（不强制兜底 16:9）
            const selectedAspectRatio = (() => {
                const ars = Array.isArray(mAspectRatios) ? (mAspectRatios as string[]) : [];
                if (ars.length === 0) return undefined;
                if (uiAspectRatio && ars.includes(uiAspectRatio)) return uiAspectRatio;
                return ars[0];
            })();
            // selectedQuality：仅当模型明确提供 qualities 时才选择一个默认；否则保持 undefined（Auto）
            const selectedQuality = (() => {
                const allQs = Array.isArray(mQualities) ? (mQualities as string[]) : [];
                const map = mQualityByDuration as Record<number, string[]> | undefined;
                const durKey = Number(selectedDuration);
                const list =
                    map && Number.isFinite(durKey) && Array.isArray(map[durKey]) && map[durKey]!.length
                        ? map[durKey]!
                        : allQs;
                if (list.length === 0) return undefined;
                if (uiQuality && list.includes(uiQuality)) return uiQuality;
                return list[0];
            })();

            // precheck 已通过，正式进入生成流程：此时才通知调用方显示 loading。
            try { onBeforeGenerate?.(); } catch {}

            /*
            precheck 已在前面完成。这里做耗时操作（强制保存 + 并发检查），
            1. 保存成功: 继续生成
            2. 保存失败/超时: 不继续生成, 并提示用户刷新
            */
            const WAIT_TIME = 10000;
            const saved = await ensureProjectSavedOrShowRefreshModal(WAIT_TIME);
            if (!saved) return false;
            // 并发检查
            if (!await checkConcurrency(project.id)) return false;

            // 仅设置需要变更的字段，updateNodeData 会浅合并，避免展开陈旧闭包数据
            updateNodeData(nodeId, {
                status: 'processing',
                updatedAt: new Date().toISOString(),
                error: undefined,
                modelUi: requestModelUi,
                runtimeEstimatedTime: undefined,
                // 清除上一次生成残留的追踪 id：否则轮询会立刻用「旧的、已完成的」generationId
                // 查到 completed + 旧结果，瞬间把节点标记完成并显示旧视频，而真正新发起的生成仍在后台运行。
                // 清空后本次生成先进入 nodeId 兜底模式（带 30s 门槛），待 202 写入新 generationId 后再正常轮询。
                processingGenerationId: undefined,
                processingTaskId: undefined,
            });

            const buildPrompt = (...segments: string[]) =>
                segments
                    .map((s) => (typeof s === 'string' ? s.trim() : ''))
                    .filter((s) => s.length > 0)
                    .join('\n');
            const videoCandidateResolved = await durationCandidates.video;
            const audioCandidateResolved = await durationCandidates.audio;
            // 组装 payload
            const resp = await fetch('/api/video/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-internal-secret': env.NEXT_PUBLIC_INTERNAL_API_SECRET },
                body: JSON.stringify({
                    projectId: project.id,
                    nodeId: nodeId,
                    modelId: modelId,
                    prompt: normalizeMentions(buildPrompt((data as any)?.instructions ?? '', ...textPrompts, ...(subjectSystemHints.length > 0 ? [] : subjectPrompts), ...subjectSystemHints)),
                    images: apiImages,
                    videos: apiVideos,
                    audios: apiAudios,
                    duration: selectedDuration,
                    durationCandidates: {
                        selected: durationCandidates.selected,
                        audio: audioCandidateResolved,
                        video: videoCandidateResolved,
                    },
                    aspectRatio: selectedAspectRatio,
                    quality: selectedQuality,
                    modelUi: requestModelUi,
                    providerOptions,
                    ...(draftSource ? { draftSource } : {}),
                }),
                signal: AbortSignal.timeout(50_000),
            });

            const response = (await resp.json()) as { error?: string; queued?: boolean; generationId?: string; estimatedTime?: number };
            if (!resp.ok || response.error) {
                const message = t(response?.error || 'Server Request failed');
                throw new Error(message);
            }

            // 写入 generationId 标记，transform.tsx 的 effect 会接管轮询兜底
            const generationId = response.generationId;
            if (generationId) {
                try { updateNodeData(nodeId, { processingGenerationId: generationId, runtimeEstimatedTime: response.estimatedTime }); } catch { }
            }
            return true;
        } catch (error) {
            // 交互式生成下，precheck 缺失输入交给调用方弹窗提醒，不写 failed、不弹 toast。
            if (error instanceof PrecheckError && onPrecheckFail) {
                try { onPrecheckFail(error.missing, error.message); } catch {}
                return false;
            }
            const errorMessage = (error instanceof Error ? error.message || 'Error generating video' : 'Error generating video');
            try {
                updateNodeData(nodeId, {
                    status: 'failed',
                    updatedAt: new Date().toISOString(),
                    error: t(errorMessage),
                    processingGenerationId: undefined,
                    runtimeEstimatedTime: undefined,
                });
            } catch {}
            if (/(insufficient)/i.test(errorMessage)) {
                try { window.dispatchEvent(new Event('open_buy_credits')); } catch { }
            }
            // 模型权限不足则打开订阅界面
            if (/(not available for your current plan)/i.test(errorMessage)) {
                try { window.dispatchEvent(new Event('open_plans_pricing')); } catch { }
            }
            handleError(t('Error generating video'), t(errorMessage));
        }
        return false;
    };

    return handleGenerate;
};